import { useContent } from '@/context/ContentContext';
import type { PageSection, PageSEO } from '@/mocks/contentManagement';

interface PageContentResult {
  /** Page sections (merged with any local dirty state) */
  sections: PageSection[];
  /** Page SEO metadata */
  seo: PageSEO | undefined;
  /** Whether content is still loading */
  loading: boolean;
  /** Error message if load failed */
  error: string | null;
  /** Get a single section by its id */
  getSection: (sectionId: string) => PageSection | undefined;
  /** Get content value for a specific section (convenience) */
  getContent: (sectionId: string) => string;
  /** Check if a specific section has unsaved changes */
  isDirty: (sectionId: string) => boolean;
}

/**
 * Hook to get dynamic content for a specific page.
 * Reads from the ContentContext which loads from API with mock fallback.
 *
 * @param pageKey — The unique page key (e.g. 'homepage', 'about', 'services')
 */
export function usePageContent(pageKey: string): PageContentResult {
  const { getSections, getSEO, loading, error, isDirty: ctxIsDirty } = useContent();

  const sections = getSections(pageKey);
  const seo = getSEO(pageKey);

  const getSection = (sectionId: string): PageSection | undefined => {
    return sections.find((s) => s.id === sectionId);
  };

  const getContent = (sectionId: string): string => {
    const section = sections.find((s) => s.id === sectionId);
    return section?.content ?? '';
  };

  const isDirty = (sectionId: string): boolean => {
    return ctxIsDirty(pageKey, sectionId);
  };

  return {
    sections,
    seo,
    loading,
    error,
    getSection,
    getContent,
    isDirty,
  };
}